// VariationKDigitNumbersTask.js
// k-cyfrowe liczby z podanych cyfr; warunek dla pierwszej cyfry wyjaśniamy w rozwiązaniu

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

// Losujemy n różnych cyfr z {0,...,9}, posortowanych rosnąco
function pickDistinctDigits(n) {
  const pool = Array.from({ length: 10 }, (_, d) => d);
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, n).sort((a, b) => a - b);
}

export function buildRepeatedDigitNumberData(k, digits) {
  const n = digits.length;
  const hasZero = digits.includes(0) ? 1 : 0;
  const firstChoices = n - hasZero;
  return { k, n, digits, hasZero, firstChoices, answer: firstChoices * Math.pow(n, k - 1) };
}

export function generateTask() {
  const k = Math.floor(Math.random() * 3) + 4; // 4..6
  const n = Math.floor(Math.random() * 8) + 3; // 3..10
  const digits = pickDistinctDigits(n);

  // jeśli 0 jest dostępne, pierwszej pozycji nie możemy wypełnić 0

  return {
    type: "VariationKDigitNumbersTask",
    data: buildRepeatedDigitNumberData(k, digits),
  };
}

export function renderTask({ data }) {
  const { k, digits } = data;
  const digitsSetLatex = `\\{${digits.join(",\\,")}\\}`;
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        Rozważamy <strong>k-cyfrowe liczby</strong> utworzone z cyfr:
      </p>
      <p><InlineMath math={digitsSetLatex} /></p>
      <p>
        Ile <InlineMath math={`${k}`} />-cyfrowych liczb można utworzyć?
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label=""
      placeholder="Wpisz liczbę liczb"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") {
    return { correct: false, message: "Podaj liczbę." };
  }
  const given = parseInt(userAnswer.raw, 10);
  if (Number.isNaN(given)) {
    return { correct: false, message: "Podaj liczbę całkowitą." };
  }
  const correct = given === data.answer;
  userAnswer.correctAnswer = data.answer;
  return { correct, message: correct ? "Dobrze!" : "Spróbuj ponownie." };
}

export function renderAnswer({ data }) {
  return <InlineMath math={`${data.answer}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={`${userAnswer.raw}`} />;
}

export function renderExplanation({ data }) {
  const { n, k, digits, hasZero, firstChoices, answer } = data;
  const digitsSetLatex = `\\{${digits.join(",\\,")}\\}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Mamy cyfry: <InlineMath math={digitsSetLatex} />. Tworzymy{" "}
        <InlineMath math={`${k}`} />-cyfrowe liczby.
      </p>

      {hasZero ? (
        <>
          <p>
            <strong>Krok 1. Dedukcja i wzór.</strong> Ponieważ{" "}
            <InlineMath math={'0'} /> jest w zbiorze, na pierwszej pozycji nie
            może stać <InlineMath math={'0'} /> (w przeciwnym razie liczba nie
            byłaby <InlineMath math={`${k}`} />-cyfrowa). Zatem pierwsza pozycja
            ma <InlineMath math={`${n}-1`} /> możliwości, a każda z pozostałych{" "}
            <InlineMath math={`${n}`} />:
          </p>
          <BlockMath math={`(n-1)\\cdot n^{\\,k-1}`} />

          <p><strong>Krok 2. Podstawienie i obliczenie.</strong></p>
          <BlockMath math={`${firstChoices} \\cdot ${n}^{${k-1}} = ${answer}`} />
        </>
      ) : (
        <>
          <p>
            <strong>Krok 1. Dedukcja i wzór.</strong> Ponieważ{" "}
            <InlineMath math={'0'} /> nie występuje w zbiorze cyfr, pierwszą
            pozycję można wypełnić dowolną z <InlineMath math={`${n}`} /> cyfr.
            Każde z <InlineMath math={`${k}`} /> miejsc ma więc{" "}
            <InlineMath math={`${n}`} /> możliwości. Otrzymujemy:
          </p>
          <BlockMath math={`n^{\\,k}`} />

          <p><strong>Krok 2. Podstawienie i obliczenie.</strong></p>
          <BlockMath math={`${n}^{${k}} = ${answer}`} />
        </>
      )}
    </div>
  );
}
